/**
 * CSS Custom Properties
 * =====================
 * Generated from tokens.ts. Injected into document <head> once at startup.
 * Every token becomes a CSS variable accessible anywhere — including plain CSS files.
 *
 * Usage in CSS:    color: var(--color-primary);
 * Usage in Tailwind: text-[var(--color-primary)]  (escape hatch for dynamic values)
 */

import { colors, fonts, radii, shadows, transitions, zIndex, animationNames } from "./tokens";

/**
 * Builds the :root CSS string from design tokens.
 * Called once in injectCSSVariables().
 */
function buildRootCSS(): string {
  return `
    :root {
      /* ── Fonts ── */
      --font-display: ${fonts.display};
      --font-body:    ${fonts.body};
      --font-mono:    ${fonts.mono};

      /* ── Brand colors ── */
      --color-primary:      ${colors.primary};
      --color-primary-700:  ${colors.primary700};
      --color-primary-50:   ${colors.primary50};
      --color-accent:       ${colors.accent};
      --color-accent-50:    ${colors.accent50};
      --color-dark:         ${colors.dark};

      /* ── Neutral colors ── */
      --color-text:         ${colors.text};
      --color-text-sub:     ${colors.textSub};
      --color-text-muted:   ${colors.textMuted};
      --color-border:       ${colors.border};
      --color-border-mid:   ${colors.borderMid};
      --color-surface:      ${colors.surface};
      --color-surface-alt:  ${colors.surfaceAlt};

      /* ── Semantic colors ── */
      --color-success:      ${colors.success};
      --color-success-50:   ${colors.success50};
      --color-warning:      ${colors.warning};
      --color-warning-50:   ${colors.warning50};
      --color-error:        ${colors.error};
      --color-error-50:     ${colors.error50};
      --color-info:         ${colors.info};
      --color-info-50:      ${colors.info50};

      /* ── Border radius ── */
      --radius-sm:          ${radii.sm};
      --radius-md:          ${radii.md};
      --radius-lg:          ${radii.lg};
      --radius-xl:          ${radii.xl};
      --radius-2xl:         ${radii["2xl"]};

      /* ── Shadows ── */
      --shadow-sm:          ${shadows.sm};
      --shadow-md:          ${shadows.md};
      --shadow-lg:          ${shadows.lg};
      --shadow-xl:          ${shadows.xl};
      --shadow-btn:         ${shadows.btn};

      /* ── Transitions ── */
      --transition-fast:    ${transitions.fast};
      --transition:         ${transitions.normal};
      --transition-slow:    ${transitions.slow};

      /* ── Z-index ── */
      --z-nav:              ${zIndex.nav};
      --z-modal:            ${zIndex.modal};
      --z-tooltip:          ${zIndex.tooltip};
    }

    /* ── Animations ── */
    @keyframes ${animationNames.fadeIn} {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes ${animationNames.slideUp} {
      from { transform: translateY(18px); opacity: 0; }
      to   { transform: translateY(0);    opacity: 1; }
    }
    @keyframes ${animationNames.slideIn} {
      from { transform: translateX(28px); opacity: 0; }
      to   { transform: translateX(0);    opacity: 1; }
    }
    @keyframes ${animationNames.popIn} {
      from { transform: scale(0.88); opacity: 0; }
      to   { transform: scale(1);    opacity: 1; }
    }
    @keyframes ${animationNames.bounce} {
      0%, 100% { transform: translateY(0); }
      50%       { transform: translateY(-6px); }
    }
    @keyframes ${animationNames.spin} {
      to { transform: rotate(360deg); }
    }
    @keyframes ${animationNames.checkPop} {
      0%   { transform: scale(0);   opacity: 0; }
      60%  { transform: scale(1.2);             }
      100% { transform: scale(1);   opacity: 1; }
    }
    @keyframes ${animationNames.pulse} {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.6; }
    }

    /* ── Global resets & base styles ── */
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
    body { font-family: var(--font-body); background: #fff; color: var(--color-text); }
    input, button, textarea, select { font-family: var(--font-body); }

    /* ── Scrollbar ── */
    ::-webkit-scrollbar         { width: 4px; height: 4px; }
    ::-webkit-scrollbar-thumb   { background: var(--color-border-mid); border-radius: 4px; }
    ::-webkit-scrollbar-track   { background: transparent; }

    /* ── Focus ring (accessibility) ── */
    :focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
      border-radius: var(--radius-sm);
    }
  `;
}

/**
 * Injects CSS variables into the document <head>.
 * Call once in main.tsx before React renders.
 */
export function injectCSSVariables(): void {
  const existing = document.getElementById("adhvi-tokens");
  if (existing) return; // already injected

  const style = document.createElement("style");
  style.id = "adhvi-tokens";
  style.textContent = buildRootCSS();
  document.head.prepend(style); // prepend so components can override
}

/**
 * Returns the raw CSS string (useful for SSR or testing).
 */
export function getCSSVariablesString(): string {
  return buildRootCSS();
}
